import { type OperationId } from "@gen/app-access-config";
import {
  CoreQuery,
  type F2fTransactionStatus,
  F2fTransactionStatusCode,
  Query,
} from "@gen/app-query";
import { f2fTransactionStatusConfig } from "@gen/transaction-status-config";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { LoadingScreen } from "~/components/loading_screen";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { StatusChip } from "~/components/status_chip";
import { useAuthOperations } from "~/contexts/auth_query";
import { TransactionMoney } from "~/entities/transaction";

import { BuyerLedger } from "../BuyerLedger";
import { ClientSection } from "../ClientSection";
import { ExtrasSection } from "../ExtrasSection";
import { ItemsSection } from "../ItemsSection";
import { SellerLedger } from "../SellerLedger";
import { TransactionTimelineSection } from "../TransactionTimeline";
import { BuyerActionsSection } from "./BuyerActionsSection";
import { NoteSection } from "./NoteSection";
import { PaymentReviewSection } from "./PaymentReviewSection";
import { SellerActionsSection } from "./SellerActionsSection";
import { UserDetails } from "./UserDetails";

interface StripePlatform {
  id: string;
  label: string;
}

const isF2fStatusCode = (
  status: string | undefined,
  statuses?: F2fTransactionStatus[],
): status is F2fTransactionStatusCode =>
  Boolean(status && statuses?.some((s) => s.code === status));

export function V2Transaction() {
  const { canExecuteAdminOperation } = useAuthOperations();

  const v2TransactionId = useParams({
    from: "/admin/v2/transactions/$transactionId",
    select: (params) => params.transactionId,
    shouldThrow: true,
  });

  const { data: v2TransactionData, isFetching: isFetchingV2 } =
    CoreQuery.useAdmin_getV2TransactionQuery(v2TransactionId);

  const f2fTransactionId = v2TransactionData?.f2f_tx_id;

  const { data: f2fTransactionData, isFetching: isFetchingF2fTransaction } =
    CoreQuery.useAdmin_getF2fTransactionQuery(f2fTransactionId!, {
      enabled: f2fTransactionId !== null && f2fTransactionId !== undefined,
    });

  const { data: chargeRatesData } =
    CoreQuery.useAdmin_getF2fTransactionChargeRatesQuery(f2fTransactionId!, {
      enabled: f2fTransactionId !== null && f2fTransactionId !== undefined,
    });

  const hasRemainder = !!f2fTransactionData?.remainder_paid;

  const stripePlatformId = f2fTransactionData?.stripe_platform_id ?? "";
  const paymentIntentId = hasRemainder
    ? (f2fTransactionData?.remainder_stripe_payment_intent_id ?? "")
    : (f2fTransactionData?.deposit_stripe_payment_intent_id ?? "");

  const isDepositInReview = f2fTransactionData?.deposit_in_review;

  const txItems = f2fTransactionData?.f2f_items ?? [];
  const hasItems = txItems.length > 0;

  const sideNavItems: SectionSideNavItem[] = useMemo(() => {
    const items: SectionSideNavItem[] = [
      { id: "summary", label: "Transaction Details" },
    ];
    if (isDepositInReview) {
      items.push({ id: "payment-review", label: "Under Review" });
    }
    if (hasItems) {
      items.push({ id: "items", label: "Items" });
    }
    items.push(
      { id: "buyer", label: "Buyer Overview" },
      { id: "seller", label: "Seller Overview" },
      { id: "notes", label: "Note" },
      { id: "timeline", label: "Timeline" },
      { id: "client", label: "Client" },
      { id: "extras", label: "Extras" },
    );
    return items;
  }, [isDepositInReview, hasItems]);

  const { data: stripePaymentIntentData } =
    CoreQuery.useAdmin_getStripePaymentIntentQuery(
      {
        stripe_platform_id: stripePlatformId,
        payment_intent_id: paymentIntentId,
      },
      {
        enabled: !!paymentIntentId && !!stripePlatformId,
      },
    );

  const { data: clientData } = CoreQuery.useAdmin_getClientQuery(
    f2fTransactionData?.client_id ?? "",
    {
      enabled: !!f2fTransactionData?.client_id,
    },
  );

  const { data: statusData } = Query.useCore_getTransactionStatusesQuery();
  const statusMap = new Map((statusData?.f2f ?? []).map((s) => [s.code, s]));

  const { data: stripePlatformsData } =
    CoreQuery.useAdmin_getStripePlatformsQuery();

  const canExecuteForStatus = useMemo(() => {
    return (operationId: OperationId): boolean => {
      if (!f2fTransactionData?.status) {
        return false;
      }

      const allowedOperations: readonly OperationId[] =
        f2fTransactionStatusConfig[f2fTransactionData.status] || [];

      return (
        allowedOperations.includes(operationId) &&
        canExecuteAdminOperation(operationId)
      );
    };
  }, [f2fTransactionData?.status, canExecuteAdminOperation]);

  const stripePlatformName = useMemo(() => {
    if (
      !f2fTransactionData?.stripe_platform_id ||
      !stripePlatformsData?.stripe_platforms
    ) {
      return "N/A";
    }
    const platform = stripePlatformsData.stripe_platforms.find(
      (sp: StripePlatform) => sp.id === f2fTransactionData.stripe_platform_id,
    );
    return platform?.label || f2fTransactionData.stripe_platform_id;
  }, [
    f2fTransactionData?.stripe_platform_id,
    stripePlatformsData?.stripe_platforms,
  ]);

  if (isFetchingV2 || isFetchingF2fTransaction) {
    return <LoadingScreen />;
  }

  if (!f2fTransactionData || !f2fTransactionId) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">Transaction not found.</Typography>
      </Stack>
    );
  }

  const transactionMoney = new TransactionMoney(f2fTransactionData);
  const statusKey = isF2fStatusCode(f2fTransactionData.status, statusData?.f2f)
    ? f2fTransactionData.status
    : undefined;
  const statusInfo = statusKey ? statusMap.get(statusKey) : undefined;

  const shouldShowStripePlatform =
    f2fTransactionData.status !== F2fTransactionStatusCode.Created &&
    f2fTransactionData.status !== F2fTransactionStatusCode.Joined;

  const f2fTransactionDataForExtras = chargeRatesData
    ? {
        ...f2fTransactionData,
        charge_rates: (({ transaction_id: _id, ...rest }) => rest)(
          chargeRatesData,
        ),
      }
    : f2fTransactionData;

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { md: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          id="summary"
          flexDirection={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2, lg: 5 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            p: { xs: 2, sm: 3, lg: 6 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            scrollMarginTop: (theme) => theme.spacing(10),
          }}
          useFlexGap
        >
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Client</Typography>
            <Typography variant="body2">
              {f2fTransactionData.client_site || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">
              Transaction total
            </Typography>
            <Typography variant="body2">
              {transactionMoney.getTotal().format()}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Description</Typography>
            <Typography variant="body2">
              {f2fTransactionData.descr || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Status</Typography>
            {statusInfo ? (
              <StatusChip label={statusInfo.label} color={statusInfo.color} />
            ) : (
              <Typography variant="body2">
                {f2fTransactionData.status}
              </Typography>
            )}
          </Stack>
          {shouldShowStripePlatform && (
            <Stack flexDirection="column" flexGrow={1}>
              <Typography variant="body1DashedDivider">
                Stripe Platform
              </Typography>
              <Typography variant="body2">{stripePlatformName}</Typography>
            </Stack>
          )}
        </Stack>
        {isDepositInReview && (
          <Box
            id="payment-review"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <PaymentReviewSection
              transactionId={f2fTransactionId}
              isDepositInReview={isDepositInReview}
            />
          </Box>
        )}

        {hasItems && (
          <Box
            id="items"
            sx={{
              mb: { xs: 3, lg: 5 },
              scrollMarginTop: (theme) => theme.spacing(10),
            }}
          >
            <ItemsSection items={txItems} />
          </Box>
        )}
        <Grid
          container
          spacing={{ xs: 3, lg: 6 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            height: "max-content",
            p: { xs: 2, lg: 0 },
          }}
        >
          <Grid
            sx={{
              pl: { xs: 0, lg: 6 },
              pt: { xs: 0, lg: 6 },
              py: { xs: 0, lg: 6 },
              scrollMarginTop: (theme) => theme.spacing(10),
            }}
            size={{ xs: 12, lg: 6 }}
            id="buyer"
          >
            <Stack spacing={5}>
              <Typography
                variant="h5SolidDivider"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PublicSvgIcon name="shopping_cart" /> Buyer overview
              </Typography>
              <UserDetails
                isF2f
                transactionData={f2fTransactionData}
                userTransactionRole="buyer"
                stripeCustomerId={stripePaymentIntentData?.customer_id ?? ""}
              />
              <BuyerLedger transactionData={f2fTransactionData} />
              <BuyerActionsSection
                canExecuteForStatus={canExecuteForStatus}
                isF2f
                transactionData={f2fTransactionData}
              />
            </Stack>
          </Grid>

          <Grid
            sx={{
              pr: { xs: 0, lg: 6 },
              pt: { xs: 0, lg: 6 },
              py: { xs: 0, lg: 6 },
              scrollMarginTop: (theme) => theme.spacing(10),
            }}
            size={{ xs: 12, lg: 6 }}
            id="seller"
          >
            <Stack spacing={{ xs: 3, lg: 5 }}>
              <Typography
                variant="h5SolidDivider"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PublicSvgIcon name="storefront" /> Seller overview
              </Typography>
              <UserDetails
                transactionData={f2fTransactionData}
                isF2f
                userTransactionRole="seller"
              />
              <SellerLedger
                transactionData={f2fTransactionData}
                allocateExtraPriceToClient={
                  f2fTransactionData?.allocate_extra_price_to_client ??
                  clientData?.allocate_extra_price_to_client
                }
                allocatePostagePriceTo={
                  f2fTransactionData?.allocate_postage_price_to ??
                  clientData?.allocate_postage_price_to
                }
              />
              <SellerActionsSection
                canExecuteForStatus={canExecuteForStatus}
                transactionData={f2fTransactionData}
                isF2f
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack spacing={{ xs: 3, lg: 5 }}>
          <Box
            id="notes"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <NoteSection transactionData={f2fTransactionData} isF2f />
          </Box>
          <Box
            id="timeline"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <TransactionTimelineSection
              timeline={f2fTransactionData.timeline || []}
              statusData={statusData}
            />
          </Box>
          <Box
            id="client"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <ClientSection transactionData={f2fTransactionData} />
          </Box>
          <Box
            id="extras"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <ExtrasSection
              transactionData={f2fTransactionDataForExtras}
              transactionId={f2fTransactionId}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
